import React, { useState } from 'react';
import { Share2, Copy, Facebook, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ShareMenuProps {
  title: string;
  willId: string;
  className?: string;
  iconSize?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const ShareMenu: React.FC<ShareMenuProps> = ({ 
  title, 
  willId, 
  className = '', 
  iconSize = 16,
  position = 'bottom'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/will/${willId}`;
  };
  
  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const url = getShareUrl();
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success("Link copied to clipboard");
        setIsOpen(false);
      })
      .catch(() => {
        toast.error("Failed to copy link");
      });
  };
  
  const handleShare = (e: React.MouseEvent, platform: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const url = getShareUrl();
    const encodedUrl = encodeURIComponent(url);
    const shareTitle = `Check out this want: ${title}`;
    const encodedTitle = encodeURIComponent(shareTitle);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        // Copy title and URL to clipboard for Facebook sharing
        const facebookShareText = `${shareTitle}\n${url}`;
        navigator.clipboard.writeText(facebookShareText)
          .then(() => {
            toast.success("Title and link copied to clipboard. Paste them in your Facebook post!");
            
            // Using the basic sharer.php since we don't have an app_id
            shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
            
            // If you get a Facebook App ID in the future, you can use this instead:
            // const appId = 'YOUR_FACEBOOK_APP_ID';
            // shareUrl = `https://www.facebook.com/dialog/share?app_id=${appId}&href=${encodedUrl}&display=popup&quote=${encodedTitle}`;
            
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
            setIsOpen(false);
          })
          .catch(() => {
            toast.error("Failed to copy link");
            
            // Still open Facebook share dialog even if clipboard fails
            shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
            setIsOpen(false);
          });
        return; // Return early as we're handling window.open in the Promise
        
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodedTitle} ${encodedUrl}`;
        break;
      default:
        // Use Web Share API if available
        if (navigator.share) {
          navigator.share({
            title: title,
            url: url
          }).catch(console.error);
          setIsOpen(false);
          return;
        } else {
          handleCopyLink(e);
          return;
        }
    }
    
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };
  
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
      }}>
        <button 
          className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${className}`}
          aria-label="Share"
        >
          <Share2 size={iconSize} className="text-gray-600" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align={position === 'left' ? 'start' : position === 'right' ? 'end' : 'center'} 
        side={position === 'top' ? 'top' : 'bottom'}
        className="bg-white border border-gray-200 shadow-md p-1 min-w-[180px]"
      >
        <DropdownMenuItem 
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-800 hover:text-white focus:bg-gray-800 focus:text-white transition-colors"
          onClick={(e) => handleShare(e as React.MouseEvent, 'facebook')}
        >
          <Facebook size={16} className="mr-2 text-blue-600 group-hover:text-white" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-800 hover:text-white focus:bg-gray-800 focus:text-white transition-colors"
          onClick={(e) => handleShare(e as React.MouseEvent, 'x')}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="currentColor" 
            className="mr-2 text-black"
          >
            <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
          </svg>
          X
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-800 hover:text-white focus:bg-gray-800 focus:text-white transition-colors"
          onClick={(e) => handleShare(e as React.MouseEvent, 'whatsapp')}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="#25D366" 
            className="mr-2 group-hover:fill-white"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem 
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-800 hover:text-white focus:bg-gray-800 focus:text-white transition-colors"
          onClick={handleCopyLink}
        >
          <Copy size={16} className="mr-2 text-gray-600" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareMenu; 